import { useContext, useEffect, type ReactElement } from 'react'
import { withZod } from '@remix-validated-form/with-zod'
import moment from 'moment'
import { z } from 'zod'
import { SmartForm } from 'app/components/form/SmartForm'
import { SchedulingWizardContext } from 'app/components/scheduling/SchedulingWizard'
import type { LocationJson, PatientJson } from 'app/types/entities'

/**
 * Permissive validator: every field on the in-app review step is prefilled and
 * read-only, so there is nothing for the user to correct. remix-validated-form
 * still requires a validator, and it must pass for the submit to fire the POST,
 * so we accept whatever is present rather than re-running the strict public
 * `patientSchema` (whose format rules would reject prefilled values).
 */
const reviewValidator = withZod(z.object({}).passthrough())

/**
 * Step 4 of the in-app scheduling wizard: a read-only summary of the prefilled
 * appointment plus a hidden SmartForm that POSTs the booking to the same endpoint
 * as the public AppointmentForm, with an added `patientId` for the linked patient.
 */
export function InAppReviewStep({
  location,
  startDate,
  patient,
  patientId,
}: {
  location?: LocationJson
  startDate?: string
  patient?: PatientJson
  patientId?: string
}): ReactElement {
  const {
    apptType,
    patientType,
    providers,
    provider,
    selectedAvailability,
    selectedDaySlotIndex,
    selectedTimeSlot,
    selectedInsurance,
    setIsFormComplete,
    setIsSaving,
    setCreatedAppointment,
    setAppointmentUnavailable,
    setAppointmentError,
    setCurrentStep,
  } = useContext(SchedulingWizardContext)

  // Everything is prefilled, but the linked patient loads asynchronously in the
  // parent. Only mark the form complete once the patient is present, so staff
  // can't Submit with empty demographics.
  useEffect(() => {
    setIsFormComplete(!!patient?.firstName)
  }, [patient, setIsFormComplete])

  const onComplete = (data: { appointment?: unknown; unavailable?: boolean; error?: unknown }) => {
    if (data.appointment) setCreatedAppointment(data.appointment as Record<string, unknown>)
    else if (data.unavailable) setAppointmentUnavailable(true)
    else if (data.error) setAppointmentError(true)
    setIsSaving(false)
    setCurrentStep(5)
  }

  const bookedProvider = providers.find((p) => p.id === selectedAvailability?.providerId)
  const providerVendorId = bookedProvider?.vendorId

  return (
    <div className="flex flex-col gap-4 p-2">
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-subtle">Patient</dt>
        <dd>
          {patient?.firstName} {patient?.lastName}
          {patient?.birthDate ? ` · ${moment(patient.birthDate).format(`MM/DD/YYYY`)}` : ``}
        </dd>
        <dt className="text-subtle">Type</dt>
        <dd>{apptType?.displayName}</dd>
        <dt className="text-subtle">Provider</dt>
        <dd>{bookedProvider?.displayName ?? provider?.displayName}</dd>
        <dt className="text-subtle">When</dt>
        <dd>
          {moment(startDate).add(selectedDaySlotIndex, `days`).format(`ddd, MMM D`)} at {selectedTimeSlot?.time}
        </dd>
        <dt className="text-subtle">Insurance</dt>
        <dd>{selectedInsurance || `—`}</dd>
      </dl>

      <SmartForm
        id="appointment-form"
        validator={reviewValidator}
        shouldPreventDefault
        reloadDocument={false}
        hideButtons
        method="POST"
        action={`/api/scheduling/locations/${location?.id}/appointment-types/${apptType?.id}/appointments`}
        onSubmit={() => setIsSaving(true)}
        onPreventDefault={onComplete}
        hiddenInputs={[
          { name: `id`, value: location?.id },
          { name: `patientId`, value: patientId },
          { name: `patientType`, value: patientType },
          { name: `firstName`, value: patient?.firstName ?? `` },
          { name: `lastName`, value: patient?.lastName ?? `` },
          { name: `email`, value: patient?.email ?? `` },
          { name: `phone`, value: patient?.cellPhone ?? `` },
          { name: `dob`, value: patient?.birthDate ? moment(patient.birthDate).format(`MM/DD/YYYY`) : `` },
          { name: `operatoryId`, value: selectedAvailability?.operatoryId },
          { name: `day`, value: moment(startDate).add(selectedDaySlotIndex, `days`).format(`YYYY-MM-DD`) },
          { name: `time`, value: selectedTimeSlot?.time },
          { name: `providerId`, value: providerVendorId },
          { name: `insurance`, value: selectedInsurance },
          { name: `hasMarketingConsent`, value: `true` },
        ]}
      />
    </div>
  )
}
